import React, { useState, useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import { usePresignDeliveryPhotoMutation } from '../../../store/slices/apiSlice';

interface DeliveryPhotoProps {
  jobId: string;
  photoUrl: string;
  alt: string;
  className?: string;
}

export const DeliveryPhoto: React.FC<DeliveryPhotoProps> = ({
  jobId,
  photoUrl,
  alt,
  className = '',
}) => {
  const [imageUrl, setImageUrl] = useState<string>(photoUrl);
  const [loading, setLoading] = useState(true);
  const [showLightbox, setShowLightbox] = useState(false);
  const [presign] = usePresignDeliveryPhotoMutation();

  useEffect(() => {
    const getSignedUrl = async () => {
      try {
        setLoading(true);
        const result = await presign({ jobId, photo_url: photoUrl }).unwrap();
        setImageUrl(result.url);
      } catch (err) {
        console.error('Failed to get signed URL:', err);
      } finally {
        setLoading(false);
      }
    };

    if (photoUrl && jobId) {
      getSignedUrl();
    } else {
      setLoading(false);
    }
  }, [jobId, photoUrl, presign]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <>
      {/* Thumbnail */}
      <img
        src={imageUrl}
        alt={alt}
        className={`${className} cursor-pointer hover:opacity-80 transition-opacity`}
        onClick={() => setShowLightbox(true)}
        onError={(e) => {
          (e.target as HTMLImageElement).src = '/placeholder-image.png';
        }}
      />

      {/* Lightbox Modal */}
      {showLightbox && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowLightbox(false)}
        >
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            onClick={() => setShowLightbox(false)}
          >
            <X className="w-6 h-6" />
          </button>

          {/* Large image */}
          <img
            src={imageUrl}
            alt={alt}
            className="max-w-full max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/placeholder-image.png';
            }}
          />

          {/* Hint text */}
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Click anywhere to close
          </p>
        </div>
      )}
    </>
  );
};
